// @ts-strict-ignore
import { PureComponent } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import type { Environment } from 'react-relay';
import { getRelayEnvironment } from '@/RelayEnvironment';
import type {
    MetaResultsLoaderQuery,
    MetaResultsLoaderQuery$data,
    ResultFilter,
} from './__generated__/MetaResultsLoaderQuery.graphql';

type MetaResults = MetaResultsLoaderQuery$data['metaResults'];
export type MetaResultsLoaderProps = {
    questionnaireId: string;
    filters: ReadonlyArray<ResultFilter>;
    shouldLoad?: boolean;
    onLoad: (metaResults: MetaResults) => void;
    onBeginLoad: () => void;
    customRelayEnvironment?: Environment;
};

/**
 * Handle callbacks since render() cannot trigger anything that changes state
 */
class MetaResultsHelper extends PureComponent<
    Pick<MetaResultsLoaderProps, 'onLoad' | 'onBeginLoad'> & {
        metaResults: MetaResults | null;
    }
> {
    load() {
        const { metaResults, onLoad, onBeginLoad } = this.props;
        if (metaResults) {
            onLoad(metaResults);
        } else {
            onBeginLoad();
        }
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate() {
        this.load();
    }

    render() {
        return null;
    }
}

type MetaResultsLoaderState = {
    shouldLoad: boolean;
    filters: ReadonlyArray<ResultFilter>;
};

class MetaResultsLoader extends PureComponent<
    MetaResultsLoaderProps,
    MetaResultsLoaderState
> {
    state = {
        shouldLoad: false,
        filters: [],
    };

    static getDerivedStateFromProps(props) {
        if (props.shouldLoad) {
            return {
                filters: props.filters,
            };
        }
        return null;
    }

    render() {
        const { questionnaireId, onLoad, onBeginLoad, customRelayEnvironment } =
            this.props;
        const { filters } = this.state;

        return (
            <QueryRenderer<MetaResultsLoaderQuery>
                query={graphql`
                    query MetaResultsLoaderQuery(
                        $questionnaireId: ID!
                        $filters: [ResultFilter!]
                    ) {
                        metaResults: metaResults(
                            questionnaireId: $questionnaireId
                            filters: $filters
                        ) {
                            metaKey
                            metaValue
                            responseCount
                        }
                    }
                `}
                environment={customRelayEnvironment || getRelayEnvironment()}
                variables={{
                    questionnaireId,
                    filters,
                }}
                render={({ error, props }) => {
                    if (error) {
                        return null;
                    }
                    return (
                        <MetaResultsHelper
                            metaResults={props?.metaResults}
                            onLoad={onLoad}
                            onBeginLoad={onBeginLoad}
                        />
                    );
                }}
            />
        );
    }
}

export default MetaResultsLoader;
